import React, { useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../../contexts/ThemeContext';
import { PersonaPicker } from '../../personas/PersonaPicker';
import { getHandleById } from '../workflowDefinitions';
import { WorkflowNodeType } from '../types';

const PersonaNode = ({ data, selected = false }) => {
  const { darkMode } = useTheme();
  const [showPicker, setShowPicker] = useState(false);
  
  // CRITICAL: Store callback in ref to survive re-renders
  const onUpdateRef = useRef(data.onUpdate);
  
  // CRITICAL: Store persona data in ref to survive re-renders
  const personaDataRef = useRef({
    personaId: data.personaId,
    personaName: data.personaName,
    personaSpecialization: data.personaSpecialization,
    personaDescription: data.personaDescription,
    personaData: data.personaData
  });
  
  // Add logging every render
  console.log('🎯 PERSONA-NODE-RENDER: Component rendered', {
    id: data.id,
    hasDataOnUpdate: !!data.onUpdate,
    hasRefCallback: !!onUpdateRef.current,
    showPicker,
    dataKeys: Object.keys(data),
    refPersonaId: personaDataRef.current.personaId,
    propsPersonaId: data.personaId
  });
  
  // Update callback ref when data changes but preserve it if it becomes undefined
  useEffect(() => {
    console.log('🎯 PERSONA-CALLBACK-EFFECT: useEffect triggered', {
      hasDataOnUpdate: !!data.onUpdate,
      hasRefCallback: !!onUpdateRef.current,
      onUpdateType: typeof data.onUpdate
    });
    
    if (data.onUpdate && typeof data.onUpdate === 'function') {
      onUpdateRef.current = data.onUpdate;
      console.log('🎯 PERSONA-CALLBACK-PRESERVED: Stored callback in ref');
    } else if (!onUpdateRef.current) {
      // Only warn if we don't have any callback at all
      console.warn('🎯 PERSONA-CALLBACK-MISSING: data.onUpdate is not available and no ref callback exists!', {
        dataOnUpdate: data.onUpdate,
        typeOfDataOnUpdate: typeof data.onUpdate,
        fullData: data
      });
    } else {
      // We have a ref callback, so just log that we're preserving it
      console.log('🎯 PERSONA-CALLBACK-PRESERVING: data.onUpdate is undefined but ref callback exists, preserving ref');
    }
  }, [data.onUpdate]);

  // Update persona data ref when we get new data from props
  useEffect(() => {
    if (data.personaId) {
      console.log('🎯 PERSONA-DATA-UPDATE: Updating persona data ref from props', {
        oldPersonaId: personaDataRef.current.personaId,
        newPersonaId: data.personaId
      });
      personaDataRef.current = {
        personaId: data.personaId,
        personaName: data.personaName,
        personaSpecialization: data.personaSpecialization,
        personaDescription: data.personaDescription,
        personaData: data.personaData
      };
    }
  }, [data.personaId, data.personaName, data.personaSpecialization, data.personaDescription]);

  const handleDef = getHandleById(WorkflowNodeType.PERSONA, 'persona_output');

  // Debug log to check data structure
  console.log('🎯 PERSONA-NODE-RENDER: Component rendered with data:', {
    id: data.id,
    hasOnUpdate: !!data.onUpdate,
    hasRefCallback: !!onUpdateRef.current,
    dataKeys: Object.keys(data),
    onUpdateType: typeof data.onUpdate,
    fullData: data
  });

  const handlePersonaSelect = (persona: any) => {
    console.log('🎯 PERSONA-SELECT-START: About to select persona:', persona);
    console.log('🎯 PERSONA-SELECT-START: Current data.onUpdate:', !!data.onUpdate);
    console.log('🎯 PERSONA-SELECT-START: Ref callback available:', !!onUpdateRef.current);
    
    // Update the persona data ref immediately to prevent flashing
    const newPersonaData = {
      personaId: persona.id,
      personaName: persona.name,
      personaSpecialization: persona.primarySpecialization,
      personaDescription: persona.shortDescription,
      personaData: persona
    };
    
    console.log('🎯 PERSONA-SELECT-UPDATE-REF: Updating persona data ref immediately', newPersonaData);
    personaDataRef.current = newPersonaData;
    
    // Always prioritize ref callback since it's more stable
    const callback = onUpdateRef.current || data.onUpdate;
    
    if (callback && typeof callback === 'function') {
      console.log('🎯 PERSONA-SELECT-CALLING: Calling callback (from ref or data)');
      callback(data.id, newPersonaData);
    } else {
      console.error('🎯 PERSONA-SELECT-ERROR: No callback available!');
      console.error('🎯 PERSONA-SELECT-ERROR: data.onUpdate:', data.onUpdate);
      console.error('🎯 PERSONA-SELECT-ERROR: onUpdateRef.current:', onUpdateRef.current);
      console.error('🎯 PERSONA-SELECT-ERROR: Full data object:', data);
      
      // Try to recover by creating a temporary callback that logs the issue
      console.warn('🎯 PERSONA-SELECT-RECOVERY: Attempting to recover by creating temporary callback');
      
      // This is a fallback - in a real scenario, we'd need the parent to handle this
      if (window.workflowDebugCallback) {
        console.log('🎯 PERSONA-SELECT-RECOVERY: Using global debug callback');
        window.workflowDebugCallback('update_persona', data.id, newPersonaData);
      } else {
        console.error('🎯 PERSONA-SELECT-RECOVERY: No recovery mechanism available');
      }
    }
  };

  return (
    <>
      <div className={`
        ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-white'} 
        border-2 
        ${
          selected 
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg shadow-purple-500/30 ring-2 ring-purple-300 dark:ring-purple-600' 
            : darkMode ? 'border-green-600' : 'border-green-300'
        } 
        rounded-lg p-3 shadow-md min-w-[180px] relative
        transition-all duration-200
        hover:shadow-lg hover:-translate-y-1
      `}>
        {/* Selection indicator badge */}
        {selected && (
          <div className="absolute -top-2 -right-2 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center shadow-lg z-10">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        {/* Handle */}
        <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <Handle
            type={handleDef.type as any}
            position={Position.Left}
            id={handleDef.id}
            style={{ background: '#555', width: '8px', height: '8px' }}
            isConnectable={data.isConnectable}
          />
        </div>
        
        {/* Content - Use ref data for display, fallback to props data */}
        <div className="font-medium">
          {(personaDataRef.current.personaId || data.personaId) ? (
            <div className="flex items-center justify-between">
              <span className="truncate font-bold text-sm tracking-wide">
                {/* Show name prominently, NEVER show UUID as primary text */}
                {personaDataRef.current.personaName || data.personaName || 'Unknown Persona'}
              </span>
              <button
                className={`ml-2 w-5 h-5 flex items-center justify-center rounded-full ${darkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log('🎯 PERSONA-SELECT: Change button clicked');
                  setShowPicker(true);
                }}
                title="Change persona"
              >
                ↺
              </button>
            </div>
          ) : (
            <button
              className={`w-full px-2 py-1 text-sm ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded font-medium`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🎯 PERSONA-SELECT: Select button clicked');
                setShowPicker(true);
              }}
            >
              Select Persona
            </button>
          )}
        </div>
        
        {/* Persona details - Enhanced with role badge and visual hierarchy */}
        {(personaDataRef.current.personaId || data.personaId) && (
          <div className="mt-2 space-y-2">
            {/* Role/Specialization Badge - visually prominent */}
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                darkMode 
                  ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50' 
                  : 'bg-blue-100 text-blue-700 border border-blue-200'
              }`}>
                {(personaDataRef.current.personaSpecialization || data.personaSpecialization || 'Contributor').split(' ')[0]}
              </span>
              
              {/* Capability count indicator */}
              {(personaDataRef.current.personaData?.capabilities || data.personaData?.capabilities) && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  darkMode 
                    ? 'bg-purple-900/30 text-purple-300' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {Object.keys(personaDataRef.current.personaData?.capabilities || data.personaData?.capabilities || {}).length} caps
                </span>
              )}
            </div>
            
            {/* Description - better typography */}
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2 leading-relaxed`}>
              {(personaDataRef.current.personaDescription || data.personaDescription || 'No description available').substring(0, 90)}
              {(personaDataRef.current.personaDescription || data.personaDescription || '').length > 90 && '...'}
            </div>
            
            {/* Tags with better visual grouping */}
            {(personaDataRef.current.personaData?.tags?.length > 0 || data.personaData?.tags?.length > 0) && (
              <div className="pt-1 border-t border-gray-200 dark:border-gray-600">
                <div className="flex flex-wrap gap-1 mt-1">
                  {(personaDataRef.current.personaData?.tags || data.personaData?.tags || []).slice(0, 3).map((tag, idx) => (
                    <span 
                      key={`${tag}-${idx}`} 
                      className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                        idx === 0 
                          ? 'bg-green-500 text-white dark:bg-green-600' 
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                  {(personaDataRef.current.personaData?.tags?.length || data.personaData?.tags?.length || 0) > 3 && (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 rounded-md text-xs">
                      +{Math.max(
                        (personaDataRef.current.personaData?.tags?.length || 0),
                        (data.personaData?.tags?.length || 0)
                      ) - 3}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Debug fallback - only if no name */}
            {!personaDataRef.current.personaName && !data.personaName && (
              <div className={`text-xs ${darkMode ? 'text-red-400' : 'text-red-500'} mt-1 font-mono bg-red-900/10 px-2 py-1 rounded`}>
                ID: {(personaDataRef.current.personaId || data.personaId || '').substring(0, 12)}...
              </div>
            )}
          </div>
        )}
      </div>

      {/* Persona Picker Modal - rendered inline */}
      {showPicker && (
        <PersonaPicker
          selectedPersonaId={personaDataRef.current.personaId || data.personaId}
          onPersonaSelect={handlePersonaSelect}
          onClose={() => {
            console.log('🎯 PERSONA-SELECT: Picker closed');
            setShowPicker(false);
          }}
          showSearch={true}
        />
      )}
    </>
  );
};

export default PersonaNode;
